import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';

/** Every server's decisions: To do and the menu read the same queries, so they say the same number */
export function useInboxes(serverIds: string[]) {
  const api = useApiClient();
  return useQueries({ queries: serverIds.map((id) => ({ queryKey: ['inbox', id], queryFn: () => api.inbox.list(id), refetchInterval: 60_000 })) });
}

export function useInbox(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['inbox', serverId], queryFn: () => api.inbox.list(serverId), refetchInterval: 60_000 });
}

/** Items set aside: by the operator (with what is missing, or done elsewhere), or by aidev */
export function useHiddenInbox(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['inbox-hidden', serverId], queryFn: () => api.inbox.hidden(serverId), refetchInterval: 300_000 });
}

type Change = { itemId: string } & ({ action: 'set-aside'; reason: string; until?: string } | { action: 'dismiss' } | { action: 'bring-back' });

export function useSetAside(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: Change) => (c.action === 'set-aside' ? api.inbox.setAside(serverId, c.itemId, { reason: c.reason, ...(c.until ? { until: c.until } : {}) })
      : c.action === 'dismiss' ? api.inbox.dismiss(serverId, c.itemId) : api.inbox.bringBack(serverId, c.itemId)),
    onSuccess: (items) => {
      qc.setQueryData(['inbox', serverId], items);
      void qc.invalidateQueries({ queryKey: ['inbox-hidden', serverId] });
    },
  });
}

/** Puts an item in another subject, or in one of its own (`own`): the operator's correction, kept */
export function useMoveToSubject(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: { itemId: string; to: string }) => api.inbox.moveToSubject(serverId, m.itemId, m.to),
    onSuccess: (items) => qc.setQueryData(['inbox', serverId], items),
  });
}
